<template>
  <div @drop.prevent="handleFileDrop" @dragover.prevent>
    <v-card :elevation="hideShowOption ? 0 : 2">
      <v-card-title class="text-h6 section-title">
        {{ $t('Supporting_Documents') }} {{ documents.length ? `(${documents.length})` : '' }}
        <template v-if="canShowShowButton">
          <v-tooltip location="bottom" class="opacity-70">
            <template v-slot:activator="{ props }">
              <v-btn icon @click="show = !show" v-bind="props" flat>
                <v-icon>{{ show ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ show ? $t('Hide') : $t('Show') }}</span>
          </v-tooltip>
        </template>        
        <v-tooltip location="bottom" class="opacity-70">
          
          <template v-slot:activator="{ props }" v-if="!disableAdd">
            <v-btn icon @click="showUpload = true" v-bind="props" elevation="0">
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Upload') }}</span>
        </v-tooltip>
        <v-tooltip location="bottom" class="opacity-70" v-if="documents.length">
          <template v-slot:activator="{ props }">
            <v-btn icon variant="text" v-bind="props" :disabled="!selectedFileNames.length" @click="downloadSelected">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Download_Selected') }}</span>
        </v-tooltip>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text v-if="show">
        <div v-if="gettingDocuments">{{ $t('Retrieving_Documents') }}...</div>
        <template v-else>
          <v-container class="hidden-md-and-up">
            <v-row dense v-if="documents.length">
              <v-col cols="12" sm="6" md="6" v-for="doc in documents" v-bind:key="doc.id">
                <v-card>
                  <v-container>
                    <v-row dense>
                      <v-col cols="1" v-if="canDelete">
                        <div>
                          <a href="#" class="noUnder" @click.prevent="deleteAttachment(doc)">
                            <v-icon size="large" class="opacity-50">mdi-delete-forever-outline</v-icon>
                          </a>
                        </div>
                      </v-col>
                      <v-col cols="1">
                        <v-checkbox-btn v-model="selectedFileNames" :value="doc.fileName" density="compact"></v-checkbox-btn>
                      </v-col>
                      <v-col>
                        <div>
                          <a href="#" class="noUnder" @click.prevent="downloadDoc(doc)">
                            {{ doc.fileName }}
                          </a>
                        </div>
                        <div>
                        {{doc.fileName}}
                        </div>
                        <div>{{doc.versions.length>0?(doc.versions[doc.versions.length-1]).comment:'hard coded value'}}</div>
                        <div v-if="showOpenText">
                          <div class="openTextLabel">{{ $t('Upload_To_OpenText') }}</div>
                          <v-radio-group
                            :model-value="openTextValue(doc)"
                            :disabled="!canEditOpenText || isSavingOpenText(doc)"
                            inline
                            density="compact"
                            hide-details
                            @update:model-value="setUploadToOpenText(doc, $event)"
                          >
                            <v-radio :label="$t('Yes')" value="yes"></v-radio>
                            <v-radio :label="$t('No')" value="no"></v-radio>
                          </v-radio-group>
                        </div>
                        <div>{{ (doc.modifiedBy || {}).displayName }}</div>
                        <div>{{ $filters.DateFilter(doc.modifiedByDate) }}</div>
                      </v-col>
                      <v-col cols="1">
                        <a href="#" class="noUnder" @click.prevent="setupDocDetails(doc)">
                          <v-icon>mdi-dots-horizontal</v-icon>
                        </a>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-col>
            </v-row>
            <v-row dense v-else>
              <v-col>
                {{ $t('No_Results') }}
              </v-col>
            </v-row>
          </v-container>
          <v-data-table class="hidden-sm-and-down" :headers="dataTableHeaders" :items="documents">
            <template v-slot:[`header.download`]>
              <div class="d-flex flex-column align-start">
                <span>{{ $t('Download') }}</span>
                <v-checkbox-btn
                  class="flex-grow-0"
                  :model-value="allSelected"
                  :indeterminate="someSelected && !allSelected"
                  density="compact"
                  @update:model-value="toggleSelectAll"
                ></v-checkbox-btn>
              </div>
            </template>
            <template v-slot:item="props">
              <tr>
                <td>
                  <a v-if="canDelete" href="#" class="noUnder" @click.prevent="deleteAttachment(props.item)">
                    <v-icon size="large" class="opacity-50">mdi-delete-forever-outline</v-icon>
                  </a>
                </td>
                <td>
                  <div class="d-flex justify-start">
                    <v-checkbox-btn
                      class="flex-grow-0"
                      v-model="selectedFileNames"
                      :value="props.item.fileName"
                      density="compact"
                    ></v-checkbox-btn>
                  </div>
                </td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(props.item)">
                    {{ props.item.fileName }}
                  </a>
                </td>
                <td>                  
                  {{props.item.versions.length>0?(props.item.versions[props.item.versions.length-1]).comment:'hard coded value'}}
                  <a v-if="canEdit(props.item.modifiedBy)" href="#" @click.prevent="setupComment(props.item)">
                    <v-icon color="accent">mdi-pencil-outline</v-icon>
                  </a>
                </td>               
                <td v-if="showOpenText">
                  <v-radio-group
                    :model-value="openTextValue(props.item)"
                    :disabled="!canEditOpenText || isSavingOpenText(props.item)"
                    inline
                    density="compact"
                    hide-details
                    @update:model-value="setUploadToOpenText(props.item, $event)"
                  >
                    <v-radio :label="$t('Yes')" value="yes"></v-radio>
                    <v-radio :label="$t('No')" value="no"></v-radio>
                  </v-radio-group>
                </td>
                <td>{{ (props.item.modifiedBy || {}).displayName }}</td>
                <td>{{ $filters.DateTimeFilter(props.item.modifiedByDate) }}</td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="setupDocDetails(props.item)">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </a>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <toaster ref="toaster"></toaster>
    <v-dialog v-model="preparingDownload" persistent max-width="400">
      <v-card>
        <v-card-text>
          <div class="mb-2">{{ $t('Preparing_Download') }}</div>
          <v-progress-linear indeterminate></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteConfirmation" persistent max-width="350">
      <v-card>
        <v-toolbar color="warning" dense>
          <v-spacer></v-spacer>
          <v-icon class="me-3" color="black">mdi-bell-alert-outline</v-icon>
        </v-toolbar>
        <v-card-text class="text-h6 font-weight-bold">{{ $t('Confirm_DeleteAttachment') }}</v-card-text>
        <v-card-text>
          {{ $t('Notification_DeletingAttachment') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="info" text class="card-button" @click="cancelDelete">{{ $t('Cancel') }}</v-btn>
          <v-btn color="error" text class="card-button" @click="confirmDelete">{{ $t('Delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDocDetails" max-width="500">
      <v-card>
        <v-toolbar color="blue-grey" dense></v-toolbar>
        <v-card-title>{{ detailsDoc.fileName }}</v-card-title>
        <v-card-text class="font-color">
          <v-container>
            <v-row dense>
              <v-col>
                <div>{{ $t('Created_By') }}: {{ (detailsDoc.insertedBy || {}).displayName }}</div>
                <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(detailsDoc.insertedByDate) }}</div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <div>{{ $t('Modified_By') }}: {{ (detailsDoc.modifiedBy || {}).displayName }}</div>
                <div>{{ $t('Modified_On') }}: {{ $filters.DateTimeFilter(detailsDoc.modifiedByDate) }}</div>
              </v-col>
            </v-row>
            <strong>{{ $t('Versions') }}</strong>
            <template v-for="(version, index) in reversedDocVersions" :key="index">
              <v-row dense align="center">
                <v-col cols="1">
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(detailsDoc, version.versionId)">
                    <v-icon>mdi-cloud-download-outline</v-icon>
                  </a>
                </v-col>
                <v-col>
                  <div>{{ $t('Created_By') }}: {{ (version.insertedBy || {}).displayName }}</div>
                  <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(version.insertedByDate) }}</div>
                  <div>
                    <pre style="white-space: pre-wrap;">{{ version.comment || '' }}</pre>
                  </div>
                  <div>{{ $t('Update_Type') }}: {{ version.updateType || 'Document Updated.' }} </div>
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-card-text>
        <v-card-actions>          
          <v-btn class="card-button" @click="showDocDetails = false">{{ $t('Close') }}</v-btn>
          <v-spacer></v-spacer>        
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEditComment" max-width="500">
      <v-card>
        <v-card-text>
          <v-container>
            <v-row dense :align="'center'">
              <v-col cols="12">
                <v-textarea
                  dense
                  variant="underlined"
                  v-model="commentUpdatedDoc.comments"
                  :label="$t('Prompt_FileComments')"
                  rows="1"
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn  color="success" class="ml-2 white--text" @click.prevent="updateComment" >{{
                  $t('Save')
                }}</v-btn>
                <v-btn color="info" variant="flat" class="mx-2" @click="clearUpdateComment">{{ $t('Cancel') }}</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
    <v-dialog v-model="showUpload" persistent max-width="700">
      <v-card @drop.prevent.stop="handleFileDrop">
        <v-card-title>
          {{ $t('Upload_File_Attachment') }}
        </v-card-title>
        <v-card-text>
          <v-container class="dense">
            <v-form ref="uploadForm" autocomplete="off">
              <v-alert v-if="showAlertFilesToParse" type="error">{{ $t('Upload_File_Limit_Quantity') }}</v-alert>
              <v-alert type="error" v-if="parsingErrors && parsingErrors.length">
                <ul>
                  <li v-for="error in parsingErrors" :key="error">{{ error }}</li>
                </ul>
              </v-alert>
              <v-row dense :align="'center'">
                <v-col class="text-center">
                  <v-btn :loading="parsingFiles" color="black" @click.prevent="openFilePicker">{{ $t('Browse_Files') }}</v-btn>
                </v-col>
              </v-row>
              <v-row :align="'center'">
                <v-col v-if="canDragAndDrop" class="text-center dropBox">
                  <div style="margin:auto;">{{ $t('Drop_Files') }}</div>
                </v-col>
              </v-row>
              
              <v-row dense v-for="(file, index) in files" :align="'center'" :key="file.id">
                <v-col cols="10" md="11">
                  <v-row dense :align="'center'">
                    <v-col cols="12" md="4">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" color="warning" v-if="isDuplicateFile(file.file.name)">mdi-alert-outline</v-icon>
                        </template>
                        <span>{{ $t('Error_DuplicateName') }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" v-show="isFailed(file)" color="red">mdi-alert-circle-outline</v-icon>
                        </template>
                        <span>{{ $t('Error_UploadDocument') }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" color="error" v-if="isBlockedFileExt(file.file.name)">mdi-alert-circle</v-icon>
                        </template>
                        <span>{{ $t('File_Extension_Blocked') }}</span>
                      </v-tooltip>

                      <v-icon v-if="isUploaded(file)" color="green">mdi-check-circle-outline</v-icon>
                      <v-progress-circular
                        color="black"
                        size="20"
                        v-if="loading[file.name]"
                        class="mb-0"
                        :model-value="progress[file.name]"
                      ></v-progress-circular>
                      {{file.file.name }}
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-textarea
                        :readonly="isUploaded(file)"
                        dense
                        variant="underlined"
                        v-model="file.comments"
                        :label="$t('Prompt_FileComments')"
                        rows="1"
                        auto-grow
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12" md="4" v-if="showOpenText">
                      <div class="openTextLabel">{{ $t('Upload_To_OpenText') }}</div>
                      <v-radio-group
                        class="openTextRadioGroup"
                        :readonly="isUploaded(file)"
                        v-model="file.openTextChoice"
                        inline
                        density="compact"
                        hide-details
                      >
                        <v-radio :label="$t('Yes')" value="yes"></v-radio>
                        <v-radio :label="$t('No')" value="no"></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="2" md="1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ props }">
                      <a href="#" class="noUnder" v-if="!submittingFiles" @click.prevent="removeFile(file)" v-bind="props">
                        <v-icon icon="mdi-close" color="grey-darken-3" />
                      </a>
                    </template>
                    <span>{{ $t('Remove') }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row>
                <v-btn :loading="submittingFiles" @click.prevent="submitFiles" :disabled="canSubmitFiles || submittingFiles">{{
                  $t('Ok_Upload')
                }}</v-btn>
                <v-btn @click.prevent="clearUpload">{{ $t('Cancel') }}</v-btn>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import MOCService from '@/services/moc.service'
import DocService from '@/services/doc.service'
import Toaster from '@/components/Toaster'
import { FormStatus, Roles } from '@/common/'
import LoadingDialog from '@/components/mocForm/LoadingDialog'
import axios from 'axios'

export default {
  name: 'SupportingDocuments',
  components: {
    Toaster,
    LoadingDialog
  },
  data() {
    return {
      show: this.hideShowOption,
      documents: [],
      gettingDocuments: false,
      showUpload: false,
      isUploadingDuplicate: false,
      showDeleteConfirmation: false,
      showDocDetails: false,
      detailsDoc: {},
      deleteDoc: {},
      files: [],
      failedFiles: [],
      successFiles: [],
      loading: {},
      progress: {},
      showAlertFilesToParse: false,
      parsingFiles: false,
      submittingFiles: false,
      blockedFileExts: ['exe', 'com', 'app', 'ipa', 'apk', 'so', 'cmd', 'bat', 'ps1', 'js', 'vbs', 'sh'],
      parsingErrors: [],
      cancelSource: [],
      showEditComment:false,
      commentUpdatedDoc:{},
      savingOpenText: {},
      selectedFileNames: [],
      preparingDownload: false
    }
  },
  props: {
    moc: {
      type: Object
    },
    hideShowOption: {
      type: Boolean,
      default: false
    },
    roles: {
      type: Array,
      default: null
    },
    showOpenText: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dataTableHeaders: function () {
      return [
        { title: '', key: '', sortable: false },
        { title: this.$t('Download'), key: 'download', sortable: false },
        { title: this.$t('File_Name'), key: 'fileName' },
        { title: this.$t('Comment'), key: 'comment' },
        ...(this.showOpenText ? [{ title: this.$t('Upload_To_OpenText'), key: 'uploadToOpenText', sortable: false }] : []),
        { title: this.$t('Modified_By'), key: 'modifiedBy' },
        { title: this.$t('Modified_On'), key: 'modifiedByDate' },
        { title: '', key: '', sortable: false }
      ]
    },
    disableAdd: function () {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      )
    },  
    
    canEditOpenText: function () {
      // intentionally no role check - anyone on the moc may answer this, but closed mocs stay immutable
      return !(
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      )
    },

    reversedDocVersions() {
      if (!this.detailsDoc.versions) return []
      return this.detailsDoc.versions.slice().reverse()
    },
    canDelete() {
      // if any type of closed, you cannot remove
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      ) {
        return false
      }
      // now that we know it is not closed, are you one of the big 3?, if so, have at it.
      if (
        this.$store.state.currentUserAdminRoles.adminRoles.filter(
          role => role === 'ProcessManager' || role === 'ProcessManagerBackup' || role === 'CommunityOwner'
        ).length > 0
      ) {
        return true
      }
      // not a member of the big 3, sorry to hear that, lets find out where/if you can remove
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value: {
          if (this.roles.includes(Roles.properties[Roles.INITIATOR].value) ||
            this.roles.includes(Roles.properties[Roles.COINITIATOR].value)
          ) {
            return true
          }
          break
        }
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
          if (this.moc.revisionHistory || this.moc.revisionHistory.length === 0) {
            if (this.roles.includes(Roles.properties[Roles.INITIATOR].value) ||
              this.roles.includes(Roles.properties[Roles.COINITIATOR].value)
            ) {
              return true
            }
            break
          }
        }
      }
      return false
    },
    canDragAndDrop: function() {
      var div = document.createElement('div')
      return ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window
    },
    canSubmitFiles: function() {
      if (this.files.length === 0) return true
      return false
    },
    canShowShowButton: function() {
      return !this.hideShowOption && this.documents.length > 0
    },
    allSelected: function() {
      return this.documents.length > 0 && this.selectedFileNames.length === this.documents.length
    },
    someSelected: function() {
      return this.selectedFileNames.length > 0
    }
  },
  methods: {
    getFiles: function() {
      if (!this.moc.mocId) return
      if (!this.gettingDocuments) {
        // get list of docs in S3 for this moc
        this.gettingDocuments = true
        return new Promise((resolve, reject) => {
          MOCService.getMocSupportingDocs(this.moc.mocId)
            .then(data => {
              this.documents = data
              this.$emit('onDocumentsLoad', data)
              resolve()
            })
            .catch(error => {
              reject(error)
              console.error(this.$t('Error_GetDocuments'), error)
            })
            .finally(() => {
              this.gettingDocuments = false
            })
        })
      }
    },
    clearUpload: function() {
      this.cancelRequests()
      this.showUpload = false
      this.isUploadingDuplicate = false
      this.files = []
      this.failedFiles = []
      this.successFiles = []
      this.loading = {}
      this.showAlertFilesToParse = false
      this.parsingErrors = []
      this.submittingFiles = false
    },
    downloadDoc: function(doc, versionId) {
      return new Promise((resolve, reject) => {
        this.$refs.loadingDialog.show()
        DocService.getPreSignedUrlForDownload(this.moc.mocId, doc.fileName, versionId)
          .then(url => {
            this.$refs.loadingDialog.hide()
            this.saveFromUrl(url, doc.fileName)
            resolve()
          })
          .catch(error => {
            this.$refs.loadingDialog.error({ metadata: 'downloadDoc', error, message: this.$t('Error_DownloadDocument') })
          })
      })
    },
    saveFromUrl: function(url, fileName) {
      const a = document.createElement('a')
      a.download = fileName
      a.href = url
      a.style.display = 'none'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    toggleSelectAll: function(value) {
      this.selectedFileNames = value ? this.documents.map(doc => doc.fileName) : []
    },
    // the service zips the selected files as it streams them out of S3, so this is a single request
    downloadSelected: async function() {
      const fileNames = this.documents.filter(doc => this.selectedFileNames.includes(doc.fileName)).map(doc => doc.fileName)
      if (!fileNames.length || this.preparingDownload) return

      this.preparingDownload = true
      let objectUrl = null

      try {
        const zip = await DocService.downloadBulk(this.moc.mocId, fileNames)
        objectUrl = URL.createObjectURL(zip)
        this.saveFromUrl(objectUrl, `${this.moc.mocId}-supporting-documents.zip`)
      } catch (error) {
        const status = error.response && error.response.status
        if (status === 413) {
          this.$refs.toaster.pop('error', this.$t('Error_DownloadTooLarge'))
        } else {
          this.$refs.toaster.pop('error', this.$t('Error_DownloadDocument'))
        }
      } finally {
        this.preparingDownload = false
        // give the browser time to start writing the file before the blob is released
        if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
      }
    },
    setupDocDetails: function(doc) {
      this.detailsDoc = doc
      this.showDocDetails = true
    },
    deleteAttachment: function(doc) {
      this.deleteDoc = doc
      this.showDeleteConfirmation = true
    },
    cancelDelete: function() {
      this.deleteDoc = {}
      this.showDeleteConfirmation = false
    },
    confirmDelete: function() {
      return new Promise((resolve, reject) => {
        this.$refs.loadingDialog.show()
        MOCService.deleteSupportingDoc(this.moc.mocId, this.deleteDoc.fileName)
          .then(success => {
            this.$refs.loadingDialog.hide()
            this.deleteDoc = {}
            this.showDeleteConfirmation = false
            resolve()
          })
          .catch(error => {
            this.$refs.loadingDialog.error({
              metadata: 'confirmDelete',
              error,
              message: this.$t('Error_DeleteDocument')
            })
          })
          .finally(() => {
            this.getFiles()
          })
      })
    },
    handleFileDrop: function(event) {
      this.showUpload = true
      let droppedFiles = event.dataTransfer.files
      this.parseFiles(droppedFiles)
    },
    parseFiles: function(filesToParse) {
      if (!filesToParse) {
        return
      }

      this.parsingErrors = []
      this.parsingFiles = true

      if (filesToParse.length > 5) {
        this.showAlertFilesToParse = true
      } else {
        for (var i = 0; i < filesToParse.length; i++) {
          var file = filesToParse[i]
          if (file.size > 268435456) {
            this.parsingErrors.push(`${file.name}: ${this.$t('File_Too_Large')}`)
            continue
          }
          // check if the file name contains undesired chars and if so, create new file with new name
          if (file.name.match(/[`~!@#$%^&*|+=?;:'",<>\{\}\[\]\\\/]/gi)) {
            const filename = file.name
              .replace(/[`~!@#$%^&*|+=?;:'",<>\{\}\[\]\\\/]/gi, '_')
              .toLowerCase()
              .trim()

            let blob = file.slice(0, file.size, file.type)
            file = new File([blob], filename, { type: file.type })
          }

          if (this.files.some(f => f.file?.name?.toLowerCase() === file.name.toLowerCase())) {
            this.parsingErrors.push(`${file.name}: ${this.$t('File_With_Same_Name')}`)
            continue
          }
          // check for blocked file extensions
          const fileName = file.name.toLowerCase()
          let isInvalid = false
          for (var blockedExt of this.blockedFileExts) {
            if (fileName.endsWith(blockedExt)) {
              isInvalid = true
              break
            }
          }
          if (isInvalid) {
            this.parsingErrors.push(`${file.name}: ${this.$t('File_Extension_Blocked')}`)
            continue
          }
          
          this.files.push({
            file, 
            comments: '',
            openTextChoice: null,
            id: Date.now() + Math.random()
          });
        }

        const uniqueErrors = new Set(this.parsingErrors)
        this.parsingErrors = [...uniqueErrors]

        this.showAlertFilesToParse = false
      }
      this.parsingFiles = false
    },
    isBlockedFileExt: function (fileName) {
      for (var blockedExt of this.blockedFileExts) {
        if (fileName.endsWith(blockedExt)) {
          return true
        }
      }
      return false
    },
    isDuplicateFile: function(fileName) {
      for (let i = 0; i < this.documents.length; i++) {
        let doc = this.documents[i]
        if (doc.fileName.toUpperCase() === fileName.toUpperCase()) return true
      }
      return false
    },
    removeFile: function(file) {
      this.files = this.files.filter(f => {
        return f !== file
      })
    },
    trackUploadProgress(fileName) {
      return progress => {
        this.progress[fileName] = progress.percentDone
      }
    },
    submitFiles: async function() {
      this.submittingFiles = true
      let filesToUpload = this.files
      if (this.successFiles.length) {
        filesToUpload = this.files.filter(f => !this.successFiles.includes(f.file.name.toLowerCase().trim()))
      }

      let promises = []

      this.failedFiles = []

      this.cancelRequests()

      for (let file of filesToUpload) {
        const fileToUpload = file.file;
        fileToUpload.comments = file.comments;
        fileToUpload.uploadToOpenText = file.openTextChoice === 'yes' ? true : file.openTextChoice === 'no' ? false : null;
        this.progress[fileToUpload.name] = 0
        this.loading[fileToUpload.name] = true

        let source = axios.CancelToken.source()
        this.cancelSource.push(source)
        const current = DocService.getPreSignedUrl(this.moc.mocId, fileToUpload.name, source.token)
          .then(url => {
            return DocService.uploadToPreSignedUrl(url, fileToUpload, this.trackUploadProgress(fileToUpload.name), source.token).then(() => {
              return MOCService.uploadSupportingDoc(this.moc.mocId, fileToUpload, source.token)
            })
          })
          .then(response => {
            this.successFiles.push(fileToUpload.name)
            return response
          })
          .catch(e => {
            console.error(e)
            this.failedFiles.push(fileToUpload.name)
          })
          .finally(() => {
            this.loading[fileToUpload.name] = false
          })

        promises.push(current)
      }

      Promise.all(promises)
        .then(() => {
          this.getFiles()
          if (this.failedFiles.length) return
          this.clearUpload()
        })
        .finally(() => {
          this.submittingFiles = false
        })
    },
    cancelRequests() {
      if (this.cancelSource) {
        this.cancelSource.forEach(ct => {
          ct.cancel('stop requests')
        })
      }
    },
    openFilePicker: function() {
      this.failedFiles = []
      let input = document.createElement('input')
      input.type = 'file'
      input.multiple = 'true'
      input.onchange = event => {
        let files = event.target.files
        this.parseFiles(files)
      }
      input.click()
    },
    isUploaded(file) {
      return this.successFiles.some(f => f === file.file.name.toLowerCase().trim())
    },
    isFailed(file) {
      return this.failedFiles.some(f => f === file.file.name.toLowerCase().trim())
    },
    clearUpdateComment:function(){
      this.commentUpdatedDoc={}
      this.showEditComment=false
    },
    openTextValue: function(doc) {
      // null leaves both radios unselected so an unanswered document stays visibly unanswered
      if (doc.uploadToOpenText === true) return 'yes'
      if (doc.uploadToOpenText === false) return 'no'
      return null
    },
    isSavingOpenText: function(doc) {
      return this.savingOpenText[doc.fileName] === true
    },
    setUploadToOpenText: function(doc, value) {
      if (value !== 'yes' && value !== 'no') return
      const newValue = value === 'yes'
      if (doc.uploadToOpenText === newValue) return
      // a second write while one is in flight would race the service's read-modify-write of this document
      if (this.savingOpenText[doc.fileName]) return

      const versions = doc.versions || []
      const lastVersion = versions[versions.length - 1] || {}
      const previousValue = doc.uploadToOpenText

      // optimistic so the radio doesn't flicker back while the update is in flight
      doc.uploadToOpenText = newValue
      this.savingOpenText[doc.fileName] = true

      return MOCService.updateSupportingDocComment(
        this.moc.mocId,
        { fileName: doc.fileName, mimeType: doc.mimeType, comments: lastVersion.comment, uploadToOpenText: newValue },
        lastVersion.versionId
      )
        .then(() => {
          // the list is not refetched, so mirror the version the service appends to keep the details dialog accurate
          versions.push({
            versionId: lastVersion.versionId,
            comment: lastVersion.comment,
            updateType: `Upload to OpenText: ${newValue ? 'Yes' : 'No'}`,
            insertedBy: { ...this.$store.state.currentUser.user },
            insertedByDate: new Date().toISOString()
          })
          doc.versions = versions
        })
        .catch(error => {
          doc.uploadToOpenText = previousValue
          console.error(error)
          this.$refs.toaster.pop('error', this.$t('Error_updateComment'))
        })
        .finally(() => {
          this.savingOpenText[doc.fileName] = false
        })
    },
    setupComment:function(commentUpdateDoc){
      this.commentUpdatedDoc = commentUpdateDoc
      this.commentUpdatedDoc.comments = (commentUpdateDoc.versions[commentUpdateDoc.versions.length-1]).comment
      this.commentUpdatedDoc.uploadToOpenText =
        commentUpdateDoc.uploadToOpenText === true || commentUpdateDoc.uploadToOpenText === false ? commentUpdateDoc.uploadToOpenText : null
      this.showEditComment = true
    },
    updateComment: async function(){    
      return new Promise((resolve,reject)=>{

        this.$refs.loadingDialog.show()
        let versionId = (this.commentUpdatedDoc.versions[this.commentUpdatedDoc.versions.length-1]).versionId
        MOCService.updateSupportingDocComment(this.moc.mocId,this.commentUpdatedDoc,versionId)
        .then(success=>{
          this.$refs.loadingDialog.hide()
          this.commentUpdatedDoc={}
          this.showEditComment=false
          resolve()

        })
        .catch(error=>{
          this.$refs.loadingDialog.error({
              metadata: 'updateComment',
              error,
              message: this.$t('Error_updateComment')
            })
          })
        .finally(()=>{
          this.getFiles()
        })

        })
      },
      canEdit:function(modifiedBy){
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      ) {
        return false
      }
      
      if (
        this.$store.state.currentUserAdminRoles.adminRoles.filter(
          role => role === 'ProcessManager' || role === 'ProcessManagerBackup' || role === 'CommunityOwner'
        ).length > 0
      ) {
        return true
      }
      if(this.roles.includes(Roles.properties[Roles.OWNER].value)){
        return true;
      }
      if(this.$store.state.currentUser.user.id === modifiedBy.id){
        return true
      }

    }    
    
  },
  created() {
    this.getFiles()
  },
  mounted() {},
  watch: {
    documents(newValue) {
      const fileNames = (newValue || []).map(doc => doc.fileName)
      this.selectedFileNames = this.selectedFileNames.filter(name => fileNames.includes(name))
    },
    files(newValue, oldValue) {
      console.log(`support doc.vue watch files`)
      if (!newValue || (newValue && !newValue.length)) {
        this.failedFiles = []
        this.successFiles = []
        this.loading = {}
      }
    }
  }
}
</script>

<style scoped>
.noUnder {
  text-decoration: none;
}
.title {
  font-size: 0.95rem !important;
  font-weight: 400;
}
.dropBox {
  border: 1px solid gray;
  margin-bottom: 10px;
}
.openTextRadioGroup :deep(.v-label) {
  white-space: nowrap;
  opacity: 1;
}
.openTextLabel {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}
.card-button{
  box-shadow: none !important;
  background-color: transparent !important;
}
.font-color{
  color: rgba(0, 0, 0, .6);
}
</style>
